import React, { useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { ChartDataset } from 'chart.js'

import Text from '../../ui/Text'
import { ConsumptionWidgetLayout } from '../consumption-widget-layout'
import { useLoadBalancerGroupGetSnapshotByDeviceIdQuery } from '../../common/redux/api/web-api'
import { MeasurementDeviceExtendedGetModel } from '../../common/redux/api/web-api'
import { LineChart } from '../../common/components/LineChart'
import useText from '../../common/hooks/useText'

export const SConsumptionTitle = styled.div`
    font-weight: 400;
    font-size: 14px;
    text-align: center;
    padding: 10px 0;
    font-family: ${({ theme }) => theme.font.extendedName};
`

export const SNoAvailableDataAlert = styled.div`
    text-align: center;
    font-size: 14px;
    padding: 25px 15px 35px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
`

export type LoadBalancerStatisticsProps = {
    graphTitle?: string
    device?: MeasurementDeviceExtendedGetModel
    className?: ClassName
    style?: React.CSSProperties
}

export function LoadBalancerStatistics(props: LoadBalancerStatisticsProps): React.ReactElement | null {
    const text = useText()

    const { graphTitle = text('lblCurrentConsumption'), device, className, style } = props
    const { data: snapShotData = [], isFetching: snapshotDataIsFetching } =
        useLoadBalancerGroupGetSnapshotByDeviceIdQuery({ measurementDeviceId: device?.id ?? 0 }, { skip: !device?.id })

    const chartData = useMemo(() => {
        const measured1 = []
        const measured2 = []
        const measured3 = []

        const charging1 = []
        const charging2 = []
        const charging3 = []

        const maxCurrentDataset = []

        const supplyMaxCurrent = device?.loadBalancerGroupSupplyMaxCurrent ?? NaN

        if (snapShotData && snapShotData.length > 0) {
            for (const item of snapShotData) {
                const x = new Date(item.timestamp!).getTime()

                measured1.push({ y: item.measurementCurrent1 ?? NaN, x })
                measured2.push({ y: item.measurementCurrent2 ?? NaN, x })
                measured3.push({ y: item.measurementCurrent3 ?? NaN, x })

                charging1.push({ y: item.chargingCurrent1 ?? NaN, x })
                charging2.push({ y: item.chargingCurrent2 ?? NaN, x })
                charging3.push({ y: item.chargingCurrent3 ?? NaN, x })

                maxCurrentDataset.push({ y: supplyMaxCurrent ?? NaN, x })
            }
        }

        const datasets: ChartDataset<'line'>[] = [
            {
                label: `L1 ${text('lblConnectorChargingStatus')}`,
                data: charging1,
            },
            {
                label: `L2 ${text('lblConnectorChargingStatus')}`,
                data: charging2,
            },
            {
                label: `L3 ${text('lblConnectorChargingStatus')}`,
                data: charging3,
            },
            {
                label: `L1 ${text('lblMeasured')}`,
                data: measured1,
            },
            {
                label: `L2 ${text('lblMeasured')}`,
                data: measured2,
            },
            {
                label: `L3 ${text('lblMeasured')}`,
                data: measured3,
            },
            {
                label: text('lblSupplyMaxCurrent'),
                data: maxCurrentDataset,
            },
        ]

        return {
            datasets,
        }
    }, [device?.loadBalancerGroupSupplyMaxCurrent, snapShotData])

    const hasData = snapShotData && snapShotData.length > 0

    const renderContent = () => {
        if (snapshotDataIsFetching) {
            return <div className="shimmer-container shimmer-small" />
        }
        return hasData ? (
            <>
                <SConsumptionTitle data-testid="title-loadbalancer">{graphTitle}</SConsumptionTitle>
                <LineChart
                    yAxisLabel={text('lblCurrentAxis')}
                    xAxisLabel={text('lblTime')}
                    data={chartData}
                    enableZoomShortcuts
                />
            </>
        ) : (
            <SNoAvailableDataAlert data-testid="no-data-loadbalancer">
                <Text code="msgNoAvailableData" />
            </SNoAvailableDataAlert>
        )
    }

    return (
        <ConsumptionWidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblLBStatisticsForAWeek')}
            dataTestId="loadbalancer"
        >
            {renderContent()}
        </ConsumptionWidgetLayout>
    )
}
